import { AllStudentRoleIds, FullAccessStudentRoleIds } from "@/constants/userRoles";
import { RouteRecordRaw } from "vue-router";

// Two gates here, on purpose. FullAccessStudentRoleIds is the default — the full student app.
// AllStudentRoleIds additionally admits BASIC_STUDENT (18) and is used ONLY on the screens that
// role's navbar config lists: Self Practice (+ the conduct screen it launches), the two student
// reports, the dashboard and the profile. Anything else stays on FullAccessStudentRoleIds.

export default [
  {
    path: "/student/assignments",
    component: () =>
      import(/* webpackChunkName: 'Assignments' */ "@/views/student/Assignments.vue"),
    name: "Assignments",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
    },
  },
  {
    path: "/student/institute-tests",
    component: () =>
      import(/* webpackChunkName: 'InstituteTests' */ "@/views/student/InstituteTests.vue"),
    name: "InstituteTests",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
    },
  },
  {
    path: "/student/conduct-activity/:activityId",
    component: () =>
      import(/* webpackChunkName: 'ConductActivity' */ "@/views/student/ConductActivity.vue"),
    name: "ConductActivity",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
      overrideBackButton: true,
      hideFooterNavigator: true,
    },
  },
  {
    path: "/student/preloaded-tests",
    component: () =>
      import(
        /* webpackChunkName: 'StudentPreloadedTests' */ "@/views/student/StudentPreloadedTests.vue"
      ),
    name: "StudentPreloadedTests",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
    },
  },
  {
    path: "/student/report-subjective-questions",
    component: () =>
      import(
        /* webpackChunkName: 'ReportSubjectiveQuestions' */ "@/views/student/ReportSubjectiveQuestions.vue"
      ),
    name: "ReportSubjectiveQuestions",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
    },
  },
  {
    path: "/student/learn-concepts-videos",
    component: () =>
      import(
        /* webpackChunkName: 'LearnConceptsVideos' */ "@/views/student/LearnConceptsVideos.vue"
      ),
    name: "LearnConceptsVideos",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
    },
  },
  {
    path: "/student/learn-doubts-revision",
    component: () =>
      import(
        /* webpackChunkName: 'LearnDoubtsRevision' */ "@/views/student/LearnDoubtsRevision.vue"
      ),
    name: "LearnDoubtsRevision",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
    },
  },
  {
    path: "/student/live-classes",
    component: () =>
      import(/* webpackChunkName: 'LiveClasses' */ "@/views/student/LiveClasses.vue"),
    name: "LiveClasses",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
    },
  },
  {
    path: "/student/past-year-papers",
    component: () =>
      import(/* webpackChunkName: 'PastYearPapers' */ "@/views/student/PastYearPapers.vue"),
    name: "PastYearPapers",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
    },
  },
  {
    path: "/student/report-effort-analysis",
    component: () =>
      import(
        /* webpackChunkName: 'ReportEffortAnalysis' */ "@/views/student/ReportEffortAnalysis.vue"
      ),
    name: "ReportEffortAnalysis",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllStudentRoleIds,
    },
  },
  {
    path: "/student/report-subject-coverage",
    component: () =>
      import(
        /* webpackChunkName: 'ReportSubjectCoverage' */ "@/views/student/ReportSubjectCoverage.vue"
      ),
    name: "ReportSubjectCoverage",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllStudentRoleIds,
    },
  },
  {
    path: "/student/self-practice",
    component: () =>
      import(/* webpackChunkName: 'SelfPractice' */ "@/views/student/SelfPractice.vue"),
    name: "SelfPractice",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllStudentRoleIds,
    },
  },
  {
    path: "/student/conduct-self-practice/:selfPracticeId",
    component: () =>
      import(
        /* webpackChunkName: 'ConductSelfPractice' */ "@/views/student/ConductSelfPractice.vue"
      ),
    name: "ConductSelfPractice",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      // Self Practice's only exit — selfPracticeStore.startSelfPractice() pushes here, so a role
      // that can reach SelfPractice must be able to reach this too.
      allowedRoleIds: AllStudentRoleIds,
      overrideBackButton: true,
      hideFooterNavigator: true,
    },
  },
  {
    path: "/student/self-tests",
    component: () => import(/* webpackChunkName: 'SelfTests' */ "@/views/student/SelfTests.vue"),
    name: "SelfTests",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
    },
  },
  {
    path: "/student/dashboard",
    component: () =>
      import(/* webpackChunkName: 'StudentDashboard' */ "@/views/student/StudentDashboard.vue"),
    name: "StudentDashboard",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllStudentRoleIds,
    },
  },
  {
    path: "/student/my-profile",
    component: () =>
      import(/* webpackChunkName: 'StudentProfile' */ "@/views/student/StudentProfile.vue"),
    name: "StudentProfile",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllStudentRoleIds,
    },
  },
  {
    path: "/student/conduct-interactive-video/:interactiveVideoId",
    component: () =>
      import(
        /* webpackChunkName: 'ConductInteractiveVideo' */ "@/views/student/ConductInteractiveVideo.vue"
      ),
    name: "ConductInteractiveVideo",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
      overrideBackButton: true,
      hideFooterNavigator: true,
      excludeNavbar: true,
    },
  },
  {
    path: "/student/interactive-videos",
    component: () =>
      import(
        /* webpackChunkName: 'StudentInteractiveVideos' */ "@/views/student/StudentInteractiveVideos.vue"
      ),
    name: "StudentInteractiveVideos",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
    },
  },
  {
    path: "/student/interactive-video-report/:activityInstanceId",
    component: () =>
      import(
        /* webpackChunkName: 'StudentInteractiveVideoReport' */ "@/views/student/StudentInteractiveVideoReport.vue"
      ),
    name: "StudentInteractiveVideoReport",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: FullAccessStudentRoleIds,
    },
  },
] as unknown as Array<RouteRecordRaw>;
